// frontend/src/pages/doctor/WritePrescription.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Save, AlertCircle } from 'lucide-react';
import doctorService from '../../services/doctorService';

const WritePrescription = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    consultationId: '',
    diagnosis: '',
    prescription: [''],
    recommendations: '',
    followUpDate: '',
    doctorNotes: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handlePrescriptionChange = (index, value) => {
    const newPrescription = [...formData.prescription];
    newPrescription[index] = value;
    setFormData(prev => ({ ...prev, prescription: newPrescription }));
  };

  const addPrescriptionLine = () => {
    setFormData(prev => ({
      ...prev,
      prescription: [...prev.prescription, '']
    }));
  };

  const removePrescriptionLine = (index) => {
    if (formData.prescription.length > 1) {
      setFormData(prev => ({
        ...prev,
        prescription: prev.prescription.filter((_, i) => i !== index)
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.consultationId.trim()) {
      setError('Consultation ID is required');
      return;
    }

    if (!formData.diagnosis.trim()) {
      setError('Diagnosis is required');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const completionData = {
        diagnosis: formData.diagnosis.trim(),
        prescription: formData.prescription.filter(med => med.trim()),
        recommendations: formData.recommendations.trim(),
        followUpDate: formData.followUpDate || undefined,
        doctorNotes: formData.doctorNotes.trim()
      };

      const response = await doctorService.completeConsultation(
        formData.consultationId.trim(),
        completionData
      );

      if (response.success) {
        alert('Prescription saved successfully!');
        navigate('/doctor/consultations');
      }
    } catch (err) {
      setError(err.message || 'Failed to save prescription');
    } finally {
      setLoading(false);
    }
  };

  const commonMedicines = [
    'Paracetamol 500mg - Take 1 tablet every 6 hours for fever',
    'Amoxicillin 500mg - Take 1 capsule 3 times daily for 7 days',
    'Ibuprofen 400mg - Take 1 tablet every 8 hours after meals',
    'Ciprofloxacin 500mg - Take 1 tablet twice daily for 5 days',
    'Omeprazole 20mg - Take 1 capsule once daily before breakfast',
    'Cetirizine 10mg - Take 1 tablet once daily at bedtime',
    'Metformin 500mg - Take 1 tablet twice daily with meals',
    'Atorvastatin 10mg - Take 1 tablet once daily at bedtime'
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Write Prescription</h1>
        <p className="text-gray-600 mt-1">Complete consultation and provide prescription</p>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-start">
          <AlertCircle className="w-5 h-5 mt-0.5 mr-3 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Consultation ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Consultation ID *
          </label>
          <input
            type="text"
            value={formData.consultationId}
            onChange={(e) => handleInputChange('consultationId', e.target.value)}
            placeholder="Enter consultation ID"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            You can find the consultation ID in the consultations list
          </p>
        </div>

        {/* Diagnosis */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Diagnosis *
          </label>
          <textarea
            value={formData.diagnosis}
            onChange={(e) => handleInputChange('diagnosis', e.target.value)}
            rows="4"
            placeholder="Provide detailed diagnosis..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            required
          />
        </div>

        {/* Prescription */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-gray-700">
              Prescription
            </label>
            <button
              type="button"
              onClick={addPrescriptionLine}
              className="flex items-center text-sm text-primary-600 hover:text-primary-700"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Medicine
            </button>
          </div>

          <div className="space-y-3">
            {formData.prescription.map((medicine, index) => (
              <div key={index} className="flex gap-2">
                <input
                  type="text"
                  value={medicine}
                  onChange={(e) => handlePrescriptionChange(index, e.target.value)}
                  placeholder="Medicine name, dosage, frequency, duration"
                  list={`medicines-${index}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <datalist id={`medicines-${index}`}>
                  {commonMedicines.map((med, i) => (
                    <option key={i} value={med} />
                  ))}
                </datalist>
                {formData.prescription.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removePrescriptionLine(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Common Medicines Helper */}
          <div className="mt-3 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs font-medium text-blue-900 mb-2">
              💡 Quick Reference - Common Medicines:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {commonMedicines.slice(0, 4).map((med, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    const emptyIndex = formData.prescription.findIndex(m => !m.trim());
                    if (emptyIndex !== -1) {
                      handlePrescriptionChange(emptyIndex, med);
                    } else {
                      setFormData(prev => ({
                        ...prev,
                        prescription: [...prev.prescription, med]
                      }));
                    }
                  }}
                  className="text-xs text-left text-blue-700 hover:text-blue-900 hover:bg-blue-100 p-2 rounded"
                >
                  {med.split(' - ')[0]}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Recommendations */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Recommendations
          </label>
          <textarea
            value={formData.recommendations}
            onChange={(e) => handleInputChange('recommendations', e.target.value)}
            rows="4"
            placeholder="Lifestyle changes, dietary advice, exercise recommendations, etc."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Follow-up Date */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Follow-up Date (Optional)
          </label>
          <input
            type="date"
            value={formData.followUpDate}
            onChange={(e) => handleInputChange('followUpDate', e.target.value)}
            min={new Date().toISOString().split('T')[0]}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>

        {/* Doctor's Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Doctor's Notes (Private)
          </label>
          <textarea
            value={formData.doctorNotes}
            onChange={(e) => handleInputChange('doctorNotes', e.target.value)}
            rows="3"
            placeholder="Internal notes for your reference..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            These notes are private and will not be shared with the patient
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={() => navigate('/doctor/consultations')}
            disabled={loading}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Saving...' : 'Save & Complete'}
          </button>
        </div>
      </form>

      {/* Tips */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 mb-2">Prescription Writing Tips</h4>
        <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
          <li>Always include medicine name, dosage, frequency, and duration</li>
          <li>Be specific about when to take medication (before/after meals, morning/evening)</li>
          <li>Include any special instructions or warnings</li>
          <li>Provide clear recommendations for lifestyle modifications</li>
          <li>Set appropriate follow-up dates based on the condition</li>
        </ul>
      </div>
    </div>
  );
};

export default WritePrescription;